/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {
  memo,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {translate} from '@ion/core/src/locale/locale';
import TertiaryButton from '../components/buttons/TertiaryButton';
import Render from '../render/Render';
import {useAppliedFilterBarStyles} from './AppliedFilterBar.styles';
import AppliedFilterBarFavoriteIcon from './AppliedFilterBarFavoriteIcon';
import {
  AppliedFilterBarFavoriteState,
  AppliedFilterBarItem,
  AppliedFilterBarProps
} from './AppliedFilterBarTypes';
import AppliedFilterChip from './AppliedFilterChip';
import AppliedFilterClearAllConfirmation, {ConfirmationAnchorAlign} from './AppliedFilterClearAllConfirmation';
import AppliedFilterOverflowModal from './AppliedFilterOverflowModal';
import useAppliedFilterBarMeasurements from './useAppliedFilterBarMeasurements';
import useAvoidElementInset from './useAvoidElementInset';

/**
 * Displays applied filter chips in a fixed one-line bar with dynamic overflow.
 *
 * ```typescript
 * import AppliedFilterBar from '@ion/dls/src/filters/AppliedFilterBar';
 *
 * <AppliedFilterBar count={2}
 *   items={items}
 *   onClearAll={clearFilters} />
 * ```
 */
export const AppliedFilterBar = ({
  count,
  items,
  onClearAll,
  favoriteState = AppliedFilterBarFavoriteState.None,
  clearAllLabel = translate('filters:selectedBar.clearAll'),
  clearAllMessage = translate('filters:selectedBar.clearAllMessage'),
  clearAllTitle = translate('filters:selectedBar.clearAllTitle'),
  hideCount = false,
  hideLabel = false,
  isModularSearch = false,
  skipClearAllConfirmation = false,
  avoidElementId
}: AppliedFilterBarProps) => {
  const [overflowOpen, setOverflowOpen]         = useState(false);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const classes                                 = useAppliedFilterBarStyles({isModularSearch});
  const contentRef                              = useRef<HTMLDivElement>(null);
  const leftControlsRef                         = useRef<HTMLDivElement>(null);
  const clearControlsRef                        = useRef<HTMLDivElement>(null);
  const moreControlRef                          = useRef<HTMLDivElement>(null);
  const moreActionRef                           = useRef<HTMLDivElement>(null);
  const chipRefs                                = useRef<Record<string, HTMLSpanElement | null>>({});
  const rootRef                                 = useRef<HTMLDivElement>(null);
  const leftInset                               = useAvoidElementInset(rootRef, avoidElementId);

  const visibleCount = useAppliedFilterBarMeasurements({
    items,
    refs : {
      chipRefs,
      clearControlsRef,
      containerRef : contentRef,
      leftControlsRef,
      moreControlRef
    }
  });

  const visibleItems = useMemo(() => items.slice(0, visibleCount), [items, visibleCount]);
  const hiddenItems  = useMemo(() => items.slice(visibleCount), [items, visibleCount]);
  const hiddenCount  = hiddenItems.length;
  const moreLabel    = translate('filters:selectedBar.more', {count: hiddenCount});

  useEffect(() => {
    if (!hiddenCount) {
      setOverflowOpen(false);
    }
  }, [hiddenCount]);

  const closeOverflow = useCallback(() => {
    setOverflowOpen(false);
  }, []);

  const requestClearAll = useCallback(() => {
    if (skipClearAllConfirmation) {
      onClearAll();
    } else {
      setConfirmationOpen(true);
    }
  }, [onClearAll, skipClearAllConfirmation]);

  const cancelClearAll = useCallback(() => {
    setConfirmationOpen(false);
  }, []);

  const openOverflow = useCallback(() => {
    setOverflowOpen(true);
  }, []);

  const confirmClearAll = useCallback(() => {
    setConfirmationOpen(false);
    onClearAll();
  }, [onClearAll]);

  const renderChip = useCallback((item: AppliedFilterBarItem): ReactElement => (
    <AppliedFilterChip key={item.id}
      className={classes.chip}
      closeIconClassName={classes.closeIcon}
      item={item} />
  ), [classes.chip, classes.closeIcon]);

  const setChipRef = useCallback((id: string) => (element: HTMLSpanElement | null) => {
    chipRefs.current[id] = element;
  }, []);

  return (
    <Render.If condition={!!items.length}>
      <div ref={rootRef}
        className={classes.root}
        style={leftInset ? {marginLeft: leftInset, width: `calc(100% - ${leftInset}px)`} : undefined}>
        <div ref={contentRef} className={classes.content}>
          <Render.If condition={!hideLabel}>
            <div ref={leftControlsRef} className={classes.labelGroup}>
              <span className={classes.label}>{translate('filters:selectedBar.label')}</span>
              <Render.If condition={!hideCount}>
                <span className={classes.count}>{count}</span>
              </Render.If>
              <div className={classes.favoriteIcon}>
                <AppliedFilterBarFavoriteIcon state={favoriteState} />
              </div>
            </div>
          </Render.If>
          <div className={classes.chipArea}>
            {visibleItems.map(renderChip)}
          </div>
          <div ref={moreControlRef} className={classes.moreSizer}>
            <TertiaryButton classes={{root: classes.barActionRoot}}>
              {translate('filters:selectedBar.more', {count: items.length})}
            </TertiaryButton>
          </div>
          <Render.If condition={!!hiddenCount}>
            <div ref={moreActionRef} className={classes.moreAction}>
              <TertiaryButton classes={{root: classes.barActionRoot}}
                onClick={openOverflow}>
                {moreLabel}
              </TertiaryButton>
            </div>
          </Render.If>
          <div ref={clearControlsRef} className={classes.clearGroup}>
            <span className={classes.divider} />
            <TertiaryButton classes={{root: classes.barActionRoot}}
              onClick={requestClearAll}>
              {clearAllLabel}
            </TertiaryButton>
          </div>
        </div>
        <div className={classes.measuring}>
          {items.map(item => (
            <span key={item.id} ref={setChipRef(item.id)}>
              {renderChip(item)}
            </span>
          ))}
        </div>
        <Render.If condition={overflowOpen}>
          <AppliedFilterOverflowModal classes={classes}
            items={hiddenItems}
            onClose={closeOverflow}
            renderChip={renderChip}
            anchorRef={isModularSearch ? moreActionRef : undefined} />
        </Render.If>
        <Render.If condition={confirmationOpen}>
          <AppliedFilterClearAllConfirmation classes={classes}
            clearAllLabel={clearAllLabel}
            message={clearAllMessage}
            title={clearAllTitle}
            anchorRef={clearControlsRef}
            anchorAlign={ConfirmationAnchorAlign.End}
            onCancel={cancelClearAll}
            onConfirm={confirmClearAll} />
        </Render.If>
      </div>
    </Render.If>
  );
};

export default memo(AppliedFilterBar);
